<script lang="ts" setup>
import { onMounted, onBeforeUnmount, ref, computed } from "vue"

const props = defineProps<{
  providerName: string
  src: string
  actualRatio?: boolean
  controls?: string[]
  autoplay?: boolean
}>()
const emit = defineEmits<{
  (e: "playing"): void
  (e: "get-ratio", ratio: number): void
}>()

const wrapper = ref<HTMLElement>()
const hasPlayed = ref(false)
const thumbnailError = ref(false)
const vimeoThumbnail = ref<string | null>(null)

const videoId = computed(() => {
  const url = props.src
  if (props.providerName === "youtube") {
    const watchMatch = url.match(/youtube\.com\/watch\?v=([^&]+)/)
    const shortMatch = url.match(/youtu\.be\/([^?]+)/)
    const embedMatch = url.match(/youtube\.com\/embed\/([^?]+)/)
    return (watchMatch ?? shortMatch ?? embedMatch)?.[1] ?? null
  }
  if (props.providerName === "vimeo") {
    const match = url.match(/vimeo\.com\/(\d+)/)
    return match?.[1] ?? null
  }
  return null
})

const embedUrl = computed(() => {
  if (!videoId.value) return null
  if (props.providerName === "youtube") {
    return `https://www.youtube-nocookie.com/embed/${videoId.value}?autoplay=1&rel=0&iv_load_policy=3&playsinline=1`
  }
  if (props.providerName === "vimeo") {
    return `https://player.vimeo.com/video/${videoId.value}?autoplay=1&dnt=1`
  }
  return null
})

const thumbnailUrl = computed(() => {
  if (props.providerName === "youtube" && videoId.value) {
    return `https://img.youtube.com/vi/${videoId.value}/hqdefault.jpg`
  }
  if (props.providerName === "vimeo") {
    return vimeoThumbnail.value
  }
  return null
})

let justExitedFullscreen = false

const onFullscreenChange = () => {
  if (!document.fullscreenElement) {
    justExitedFullscreen = true
    setTimeout(() => {
      justExitedFullscreen = false
    }, 300)
  }
}

const stopPlayer = () => {
  if (document.fullscreenElement || justExitedFullscreen) return
  if (!wrapper.value?.isConnected) return
  hasPlayed.value = false
}

const onPlayButtonClick = () => {
  hasPlayed.value = true
  emit("playing")
}

onMounted(async () => {
  if (props.autoplay) {
    hasPlayed.value = true
  }

  try {
    const url = props.src
    const oembedUrl =
      props.providerName === "vimeo"
        ? `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`
        : `https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`
    const res = await fetch(oembedUrl)
    if (res.ok) {
      const data = await res.json()
      if (data.width && data.height) {
        emit("get-ratio", data.height / data.width)
      }
      if (data.thumbnail_url) {
        vimeoThumbnail.value = data.thumbnail_url
      }
    }
  } catch {}

  window.addEventListener("slideshow-slide-change", stopPlayer)
  document.addEventListener("fullscreenchange", onFullscreenChange)
})

onBeforeUnmount(() => {
  window.removeEventListener("slideshow-slide-change", stopPlayer)
  document.removeEventListener("fullscreenchange", onFullscreenChange)
})
</script>

<template>
  <div ref="wrapper" class="relative mx-auto" style="aspect-ratio: 16/9">
    <iframe
      v-if="hasPlayed && embedUrl"
      :src="embedUrl"
      class="absolute inset-0 w-full h-full"
      frameborder="0"
      allow="autoplay; fullscreen; picture-in-picture"
      allowfullscreen
    />
    <template v-else>
      <img
        v-if="thumbnailUrl && !thumbnailError"
        :src="thumbnailUrl"
        class="absolute inset-0 w-full h-full object-cover border"
        @error="thumbnailError = true"
      />
      <div v-else class="absolute inset-0 w-full h-full bg-black" />
    </template>
    <button
      v-show="!hasPlayed"
      class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 p-4 border cursor-pointer bg-white md:hover:bg-black md:hover:text-white"
      aria-label="Play"
      @click="onPlayButtonClick"
    >
      <div class="w-8 h-8">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="2 2 20 20"
          stroke="currentColor"
          fill="transparent"
          class="w-full h-full stroke-1"
        >
          <path d="M8 5v14l11-7z" vector-effect="non-scaling-stroke" />
        </svg>
      </div>
    </button>
  </div>
</template>
